import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Pagination, SkeletonText } from '@/components';
import { useResizableColumns } from '@/common/hooks';

const CELL = 'align-middle border-b border-r last:border-r-0';

function cellAlign(col, tight = false) {
  if (tight) {
    return col.align === 'center' ? 'text-center px-2' : 'text-left px-3';
  }
  return col.align === 'center' ? 'text-center px-2' : 'text-left px-3.5';
}

function isCompactCol(col) {
  const id = String(col.id || '').toLowerCase();
  const label = String(col.label || '')
    .trim()
    .toLowerCase();
  if (
    ['sr', 'srno', 'select', 'checkbox', 'check', 'action', 'actions'].includes(
      id,
    )
  ) {
    return true;
  }
  if (!label) return true;
  if (/^sr\.?\s*no/.test(label)) return true;
  return label === 'action' || label === 'actions';
}

function loadExplicitResized(storageKey) {
  if (!storageKey) return new Set();
  try {
    const explicit = JSON.parse(
      localStorage.getItem(`${storageKey}__resized`) || 'null',
    );
    return Array.isArray(explicit) ? new Set(explicit) : new Set();
  } catch {
    return new Set();
  }
}

function splitByWeight(columns, containerWidth) {
  const weights = columns.map((col) => col.defaultWidth || 1);
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  const next = weights.map((weight) =>
    Math.floor((containerWidth * weight) / weightSum),
  );
  const used = next.reduce((sum, width) => sum + width, 0);
  next[next.length - 1] += containerWidth - used;
  return next.map((width, index) =>
    Math.max(width, columns[index].minWidth || 0),
  );
}

function loadResizedIds(storageKey, columns) {
  if (!storageKey) return new Set();
  try {
    const explicit = localStorage.getItem(`${storageKey}__resized`);
    if (explicit) {
      const ids = JSON.parse(explicit);
      if (Array.isArray(ids)) return new Set(ids);
    }
    const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (!saved || typeof saved !== 'object') return new Set();
    return new Set(
      columns
        .filter(
          (col) =>
            typeof saved[col.id] === 'number' &&
            saved[col.id] !== col.defaultWidth,
        )
        .map((col) => col.id),
    );
  } catch {
    return new Set();
  }
}

function rowKeyOf(row, rowKey, index) {
  if (typeof rowKey === 'function') return rowKey(row, index);
  if (rowKey && row?.[rowKey] != null) return row[rowKey];
  return index;
}

/**
 * Freshsales-style resizable table. Page supplies columns and cell renderers.
 * Column: { id, label, defaultWidth, minWidth, resizable, align, header, cell, skeleton, stopPropagation }
 */
export function V2Table({
  columns,
  data = [],
  rowKey = 'id',
  storageKey,
  loading = false,
  loadingRowCount = 10,
  notice = null,
  empty = 'No records found',
  onRowClick,
  isRowSelected,
  rowId,
  rowClassName,
  pagination = null,
  bare = false,
  compact = false,
  tight = false,
  fill = false,
  /** Size to rows. Pagination stays in document flow. No viewport footer. */
  flow = false,
  headerVariant = 'dark',
  fitProportional = false,
  className = '',
}) {
  const scrollerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [resizedIds, setResizedIds] = useState(() =>
    fitProportional
      ? loadExplicitResized(storageKey)
      : loadResizedIds(storageKey, columns),
  );
  const fittingRef = useRef(resizedIds.size === 0);

  const markResized = useCallback(
    (colId) => {
      setResizedIds((prev) => {
        if (prev.has(colId)) return prev;
        const next = new Set(prev);
        next.add(colId);
        if (storageKey) {
          try {
            localStorage.setItem(
              `${storageKey}__resized`,
              JSON.stringify([...next]),
            );
          } catch {
            // keep the in-memory set if storage is blocked
          }
        }
        return next;
      });
    },
    [storageKey],
  );

  const { columnWidths, resizingCol, onStartResize, commitWidths } =
    useResizableColumns(storageKey, columns, markResized);

  if (resizedIds.size > 0) fittingRef.current = false;

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const next = el.clientWidth;
      setContainerWidth((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const darkHeader = headerVariant === 'dark';
  const line = darkHeader ? 'border-[#DCE5EF]' : 'border-[#E2E8F0]';

  const fitting =
    fitProportional && fittingRef.current && resizedIds.size === 0;

  const displayWidths = useMemo(() => {
    if (fitting && containerWidth > 0) {
      return splitByWeight(columns, containerWidth);
    }

    const base = columns.map((col) => {
      if (col.grow === false) return col.defaultWidth || col.minWidth || 100;
      return columnWidths[col.id] || col.defaultWidth || 100;
    });
    if (fitting || fitProportional) return base;

    const baseSum = base.reduce((sum, width) => sum + width, 0);
    const slack =
      containerWidth > 0 ? Math.max(0, containerWidth - baseSum) : 0;
    if (!slack) return base;

    const flexible = columns
      .map((col, index) => ({ col, index }))
      .filter(
        ({ col }) =>
          col.grow !== false && !isCompactCol(col) && col.id !== resizingCol,
      );
    const fresh = flexible.filter(({ col }) => !resizedIds.has(col.id));
    const targets = fresh.length ? fresh : flexible;
    if (!targets.length) return base;

    const weight = targets.reduce(
      (sum, { col }) => sum + (col.defaultWidth || 1),
      0,
    );
    const next = base.slice();
    let assigned = 0;
    targets.forEach(({ col, index }, position) => {
      const share =
        position === targets.length - 1
          ? slack - assigned
          : Math.round((slack * (col.defaultWidth || 1)) / weight);
      assigned += share;
      next[index] += share;
    });
    return next;
  }, [
    columns,
    columnWidths,
    containerWidth,
    resizedIds,
    resizingCol,
    fitting,
    fitProportional,
  ]);

  const startResize = (colId, event) => {
    if (fitting && containerWidth > 0) {
      const map = {};
      columns.forEach((col, index) => {
        map[col.id] = displayWidths[index];
      });
      commitWidths(map);
      fittingRef.current = false;
    }
    onStartResize(colId, event);
  };

  const totalWidth = displayWidths.reduce((sum, width) => sum + width, 0);
  const showNotice = Boolean(notice);
  const showEmpty = !loading && !showNotice && data.length === 0;
  const headerOffset = tight
    ? 'top-[42px]'
    : darkHeader
      ? 'top-12'
      : compact
        ? 'top-9'
        : 'top-[50px]';
  const fillByRatio =
    fitProportional &&
    !resizingCol &&
    containerWidth > 0 &&
    totalWidth > 0 &&
    totalWidth <= containerWidth;
  const widthStyles = fillByRatio
    ? (() => {
        const percents = displayWidths.map(
          (width) => (width / totalWidth) * 100,
        );
        const styles = percents.map((percent, index) =>
          index === percents.length - 1 ? 0 : Math.floor(percent * 100) / 100,
        );
        const used = styles.reduce((sum, percent) => sum + percent, 0);
        styles[styles.length - 1] = Math.round((100 - used) * 100) / 100;
        return styles.map((percent) => `${percent}%`);
      })()
    : displayWidths.map((width) => `${width}px`);

  return (
    <div
      className={
        bare
          ? `w-full min-w-0 flex flex-col ${flow ? '' : 'flex-1 min-h-0 overflow-hidden'} ${darkHeader ? 'rounded-xl border border-[#DCE5EF]' : ''} ${className}`
          : `w-full min-w-0 rounded-2xl bg-white border border-[#E2E8F0] shadow-sm flex flex-col ${flow ? '' : 'flex-1 min-h-0 overflow-hidden'} ${className}`
      }
    >
      <div
        className={
          flow
            ? 'relative w-full min-w-0'
            : `relative flex-1 min-h-0 ${fill ? 'h-full' : ''}`
        }
      >
        <div
          ref={scrollerRef}
          className={
            flow
              ? 'w-full min-w-0 overflow-x-auto overflow-y-visible custom-scrollbar relative'
              : `h-full overflow-x-auto overflow-y-auto custom-scrollbar relative ${fill ? 'h-full' : ''}`
          }
        >
          <table
            className={`text-left border-separate border-spacing-0 ${fill ? 'h-full' : ''}`}
            style={{
              tableLayout: 'fixed',
              width: fillByRatio ? '100%' : `${totalWidth}px`,
              minWidth: fillByRatio ? '100%' : `${totalWidth}px`,
            }}
          >
            <colgroup>
              {columns.map((col, index) => (
                <col key={col.id} style={{ width: widthStyles[index] }} />
              ))}
            </colgroup>
            <thead
              className={`sticky top-0 z-20 ${darkHeader ? 'bg-[#203126]' : 'bg-[#F8FAFC]'}`}
            >
              <tr
                className={`${tight ? 'h-[42px]' : darkHeader ? 'h-12 laptop:!h-10' : compact ? 'h-9 laptop:!h-10' : 'h-[50px] laptop:!h-10'} ${darkHeader ? 'bg-[#203126]' : 'bg-[#F8FAFC] border-b border-[#E2E8F0]'}`}
              >
                {columns.map((col, colIndex) => {
                  const width = widthStyles[colIndex];
                  const edge = darkHeader
                    ? `border-r border-white/25 last:border-r-0 ${colIndex === 0 ? 'rounded-tl-xl' : ''} ${colIndex === columns.length - 1 ? 'rounded-tr-xl' : ''}`
                    : 'border-b border-[#E2E8F0] border-r border-[#E2E8F0] last:border-r-0';
                  return (
                    <th
                      key={col.id}
                      style={{ width }}
                      className={`relative ${tight ? 'py-2 text-[13px]' : darkHeader ? 'py-3 laptop:!py-2 text-sm' : compact ? 'py-1.5 text-[13px]' : 'py-3 laptop:!py-2 text-[13px]'} select-none font-semibold ${cellAlign(col, tight)} ${
                        darkHeader
                          ? `bg-[#203126] text-white ${edge}`
                          : `bg-[#F8FAFC] text-[#475569] ${edge}`
                      }`}
                    >
                      {col.header ? (
                        col.header()
                      ) : (
                        <div
                          className={`flex items-center gap-1 overflow-hidden ${
                            col.align === 'center'
                              ? 'justify-center'
                              : 'justify-between'
                          }`}
                        >
                          <span
                            className={`truncate block ${col.headerClassName || ''}`}
                            title={col.label}
                          >
                            {col.label}
                          </span>
                        </div>
                      )}
                      {col.grow === false ? null : (
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${col.label || 'column'}`}
                          onPointerDown={(e) => startResize(col.id, e)}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className={`absolute right-0 top-0 bottom-0 w-3 cursor-col-resize select-none touch-none flex items-center justify-center z-10 ${
                            resizingCol === col.id
                              ? 'opacity-100'
                              : 'opacity-0 hover:opacity-100'
                          }`}
                          title="Drag to resize column"
                        >
                          <div
                            className={`w-[2px] h-4 rounded-full transition-all duration-150 ${
                              resizingCol === col.id
                                ? 'bg-[#94A3B8]'
                                : 'bg-[#CBD5E1]'
                            }`}
                          />
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white text-sm text-[#1F2241]">
              {showNotice || showEmpty ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className={
                      showNotice ? 'py-16 border-b border-[#E2E8F0]' : 'py-24'
                    }
                  />
                </tr>
              ) : loading ? (
                Array.from({ length: loadingRowCount }, (_, i) => (
                  <tr
                    key={`skeleton-${i}`}
                    className={`${fill ? '' : tight ? 'h-11' : compact ? 'h-14 laptop:!h-12' : 'h-[60px] sm:h-[64px] laptop:!h-12'} bg-white`}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={`${CELL} ${line} ${tight ? 'py-2' : compact ? 'py-1' : 'py-3.5 sm:py-4 laptop:!py-2'} ${cellAlign(col, tight)}`}
                      >
                        {col.skeleton ?? (
                          <SkeletonText width="w-20" height="h-4" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                data.map((row, index) => {
                  const selected = isRowSelected?.(row, index);
                  const extraClass = rowClassName?.(row, index) || '';
                  return (
                    <tr
                      key={rowKeyOf(row, rowKey, index)}
                      id={rowId?.(row, index)}
                      onClick={
                        onRowClick ? () => onRowClick(row, index) : undefined
                      }
                      className={`${fill ? '' : tight ? 'h-11' : compact ? 'h-14 laptop:!h-12' : 'h-[60px] sm:h-[64px] laptop:!h-12'} transition-colors ${
                        onRowClick ? 'cursor-pointer' : ''
                      } ${
                        selected
                          ? 'bg-[#EBF4EA]/60 hover:bg-[#EBF4EA]/80'
                          : 'bg-white hover:bg-[#F8FAFC]'
                      } ${extraClass}`}
                    >
                      {columns.map((col) => (
                        <td
                          key={col.id}
                          className={`${CELL} ${line} ${tight ? 'py-2' : compact ? 'py-1' : 'py-3.5 sm:py-4 laptop:!py-2'} ${cellAlign(col, tight)}`}
                          onClick={
                            col.stopPropagation
                              ? (e) => e.stopPropagation()
                              : undefined
                          }
                        >
                          {col.cell ? col.cell(row, index) : null}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {showNotice || showEmpty ? (
          <div
            className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center justify-center overflow-hidden px-4 py-6 ${headerOffset}`}
          >
            <div className="pointer-events-auto min-w-0 max-w-full whitespace-normal break-words text-center text-text-muted">
              {showNotice ? notice : empty}
            </div>
          </div>
        ) : null}
      </div>
      {pagination ? (
        <Pagination
          currentPage={pagination.currentPage}
          totalCount={pagination.totalCount}
          itemsPerPage={pagination.itemsPerPage ?? 10}
          onChange={pagination.onChange}
          className={`bg-white shrink-0 border-t border-[#E2E8F0] !py-2 !px-4 ${flow ? '' : 'mt-auto'}`}
        />
      ) : null}
    </div>
  );
}
